import { useQuery } from "@tanstack/react-query";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { chunkArray } from "@/utilities/chunkArray";

const RESULTS_LIMIT = 50000;

const ID_CHUNK_SIZE = 5000;

/** A month is enough: a tachograph reports its make and calibration date with every trip. */
const WINDOW_DAYS = 30;

/**
 * Readings of the given tachograph diagnostics per vehicle.
 *
 * GetLatestStatusDataByDevice does not carry these — it answers with the device row and an empty
 * sensor list for tachograph diagnostics — so the readings are fetched over a short window.
 * One request per diagnostic: a list of ids in the diagnostic search answers with nothing.
 */
export function useTachographDeviceStatusData(deviceIds: string[], diagnosticIds: string[]) {
  const { geotabClient, activeSessionId } = useGeotabClient();

  return useQuery({
    queryKey: ["tachographDeviceStatusData", activeSessionId, deviceIds, diagnosticIds],

    queryFn: async ({ signal }) => {
      const toDate = new Date();
      const fromDate = new Date(toDate.getTime() - WINDOW_DAYS * 24 * 60 * 60 * 1000);

      const requests = diagnosticIds.flatMap(diagnosticId =>
        chunkArray(deviceIds, ID_CHUNK_SIZE).map(ids =>
          geotabClient.get(
            "StatusData",
            {
              diagnosticSearch: { id: diagnosticId },
              deviceSearch: { deviceIds: ids },
              fromDate,
              toDate,
            },
            RESULTS_LIMIT,
            signal,
          ),
        ),
      );

      const pages = await Promise.all(requests);

      return pages.flat();
    },

    enabled: deviceIds.length > 0 && diagnosticIds.length > 0,
  });
}
